import { colors, Typography } from '@sweep-io/sweep-design';
import { AdvancedFilter } from '../common/filters/advanced-filter/AdvancedFilter';
import { Box } from '@mui/material';
import { ReactNode, useMemo } from 'react';
import _uniq from 'lodash/uniq';
import { globalSearchSx } from '../pages/canvas-pages/canvasPanelConsts';
import { Issue } from './issuesTypes';
import { resourceTypeToLabel } from './issuesHelper';
import capitalize from 'lodash/capitalize';
import { IssueStatusValues } from '@server/issues';

export type IssuesFiltersType = {
  selectedConfigurationTypes: string[];
  selectedIssues: string[];
  selectedSeverity: string[];
  selectedStatus: string[];
};

const IssuesFilters = ({
  filters,
  setFilter,
  issues,
  searchJsx,
}: {
  filters: IssuesFiltersType;
  setFilter: (filter: Partial<IssuesFiltersType>) => void;
  issues: Issue[];
  searchJsx: ReactNode;
}) => {
  const { configurationTypesItems, issuesItems, severityItems, statusItems } = useMemo(() => {
    const uniqueTypes = _uniq(issues.map((issue) => issue.resourceType));
    const configurationTypesItems = uniqueTypes.map((type) => ({
      value: type,
      label: resourceTypeToLabel[type],
    }));

    const uniqueIssues = _uniq(issues.map((issue) => issue.bestPracticeName));
    const issuesItems = uniqueIssues.map((issue) => ({
      value: issue,
      label: issue,
    }));

    const uniqueSeverity = _uniq(issues.map((issue) => issue.severity));
    const severityItems = uniqueSeverity.map((severity) => ({
      value: severity,
      label: severity,
    }));

    const statusItems = IssueStatusValues.map((status) => ({
      value: status,
      label: capitalize(status),
    }));

    return {
      configurationTypesItems,
      issuesItems,
      severityItems,
      statusItems,
    };
  }, [issues]);

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        ...globalSearchSx(false),
      }}
    >
      <Box
        sx={{
          padding: '8px 16px 8px 8px',
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
        }}
      >
        <Box display="flex" gap={0.5} alignItems="center">
          <Typography variant="caption" color={colors.grey[900]}>
            Type
          </Typography>
          <AdvancedFilter
            items={configurationTypesItems}
            selectedItems={filters.selectedConfigurationTypes}
            onSelectedItemsChange={(items) => setFilter({ selectedConfigurationTypes: items })}
            maxWidth="50px"
          />
        </Box>
        <Box display="flex" gap={0.5} alignItems="center">
          <Typography variant="caption" color={colors.grey[900]}>
            Issue
          </Typography>
          <AdvancedFilter
            items={issuesItems}
            selectedItems={filters.selectedIssues}
            onSelectedItemsChange={(items) => setFilter({ selectedIssues: items })}
            maxWidth="50px"
          />
        </Box>
        <Box display="flex" gap={0.5} alignItems="center">
          <Typography variant="caption" color={colors.grey[900]}>
            Impact
          </Typography>
          <AdvancedFilter
            items={severityItems}
            selectedItems={filters.selectedSeverity}
            onSelectedItemsChange={(items) => setFilter({ selectedSeverity: items })}
            maxWidth="50px"
          />
        </Box>
        <Box display="flex" gap={0.5} alignItems="center">
          <Typography variant="caption" color={colors.grey[900]}>
            Status
          </Typography>
          <AdvancedFilter
            items={statusItems}
            selectedItems={filters.selectedStatus}
            onSelectedItemsChange={(items) => setFilter({ selectedStatus: items })}
            maxWidth="50px"
          />
        </Box>
      </Box>
      <Box width="200px">{searchJsx}</Box>
    </Box>
  );
};

export default IssuesFilters;
